import { Flex, Input as AntInput } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { forwardRef, useEffect, useRef, useState } from 'react';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

function parseDate(value, format) {
  if (!value) return { day: '', month: '', year: '' };
  const d = format ? dayjs(value, format) : dayjs(value);
  if (!d.isValid()) return { day: '', month: '', year: '' };
  return {
    day: String(d.date()).padStart(2, '0'),
    month: String(d.month() + 1).padStart(2, '0'),
    year: String(d.year()),
  };
}

function parseDateInput(dateInput) {
  if (typeof dateInput === 'string') {
    const isoRegex = /^(\d{3,4})-(\d{2})-(\d{2})$/;
    const match = dateInput.match(isoRegex);

    if (match) {
      return {
        year: match[1],
        month: match[2],
        day: match[3],
      };
    }
    return null;
  }

  if (typeof dateInput === 'object' && dateInput !== null) {
    return dateInput;
  }

  return null;
}

export const validateDateInput = (dateInput) => {
  const errors = { day: '', month: '', year: '' };

  const parsedDate = parseDateInput(dateInput);
  if (!parsedDate) {
    errors.day = 'Invalid date format. Use "MM-DD-YYYY"';
    return errors;
  }

  const { day, month, year } = parsedDate;

  if (!year) {
    errors.year = 'Year is required';
  }

  if (!month) errors.month = 'Month is required';
  if (!day) errors.day = 'Day is required';

  if (errors.day || errors.month || errors.year) return errors;

  const m = Number(month);
  if (m < 1 || m > 12) {
    errors.month = 'Month must be between 1 and 12';
  }

  if (!errors.month) {
    const y = Number(year);
    const maxDays = dayjs(`${y}-${String(m).padStart(2, '0')}`, 'YYYY-MM').daysInMonth() || 30;
    const d = Number(day);
    if (d < 1 || d > maxDays) {
      errors.day = `Day must be between 1 and ${maxDays}`;
    }
  }

  if (!errors.day && !errors.month && !errors.year) {
    const y = Number(year);
    const d = Number(day);
    const birthDate = dayjs(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`, 'YYYY-MM-DD');
    const age = dayjs().diff(birthDate, 'year');

    if (age < 18) {
      errors.year = 'Age must be at least 18 years old';
    } else if (age >= 150) {
      errors.year = 'Age must be less than 150 years old';
    }
  }

  return errors;
};

function buildDate({ day = '', month = '', year = '' }, format) {
  const errors = validateDateInput({ day, month, year });

  if (errors.day || errors.month || errors.year) {
    return { errors };
  }

  const m = Number(month);
  const d = Number(day);
  const y = Number(year);

  const date = dayjs.utc(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`, 'YYYY-MM-DD');

  return { value: format ? date.format(format) : date.toISOString() };
}

const Input = forwardRef((props, ref) => {
  return <AntInput {...props} ref={ref} />;
});

const DateInput = ({ value, onChange, errorText, containerClassName = '', className = '', format }) => {
  const [localDate, setLocalDate] = useState(parseDate(value, format));
  const inputsRef = useRef([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (value && !loaded) {
      setLoaded(true);
      setLocalDate(parseDate(value, format));
    }
  }, [value]);

  const dateFields = [
    { name: 'month', label: 'Month', maxLength: 2 },
    { name: 'day', label: 'Day', maxLength: 2 },
    { name: 'year', label: 'Year', maxLength: 4 },
  ];

  const getInputNode = (el) => {
    if (!el) return null;
    if (el.input) return el.input;
    return el;
  };

  const focusNext = (index) => {
    const node = getInputNode(inputsRef.current[index + 1]);
    if (!node) return;
    node.focus();
    const len = node.value.length;
    node.setSelectionRange(len, len);
  };

  const focusPrev = (index) => {
    const node = getInputNode(inputsRef.current[index - 1]);
    if (!node) return;
    node.focus();
    const len = node.value.length;
    setTimeout(() => {
      node.setSelectionRange(len, len);
    });
  };

  const getCursorIndex = (inputId) => {
    const input = document.getElementById(inputId);
    if (!input) {
      return null;
    }

    const cursorIndex = input.selectionStart;
    return cursorIndex;
  };

  const onDateChange = (e, index, maxLength) => {
    const { name, value } = e.target;

    if (!/^\d*$/.test(value)) return;

    setLocalDate((prev) => ({
      ...prev,
      [name]: value,
    }));

    setTimeout(() => {
      if (value.length === maxLength) {
        focusNext(index);
      }
    });
  };

  const onKeyDown = (e, index) => {
    const cursorIndex = getCursorIndex(e.target.id);
    if (e.key === 'Backspace' && !e.target.value) {
      focusPrev(index);
    } else if (e.key === 'Enter') {
      focusNext(index);
    } else if (e.key === 'ArrowLeft' && cursorIndex === 0) {
      focusPrev(index);
    } else if (e.key === 'ArrowRight' && cursorIndex === 2) {
      focusNext(index);
    }
  };

  const onBlur = () => {
    if (!localDate.day && !localDate.month && !localDate.year) {
      return onChange('0000-00-00');
    }
    const result = buildDate({ day: localDate.day, month: localDate.month, year: localDate.year }, format);
    if (result?.value) {
      onChange?.(result.value);
    } else {
      onChange(`${localDate?.year || '0000'}-${localDate?.month || '00'}-${localDate?.day || '00'}`);
    }
  };

  return (
    <Flex justify="space-between" align="start" className={clsx(containerClassName, 'w-100')} gap={10}>
      {dateFields.map((field, index) => (
        <Flex key={field.name} vertical className="w-100">
          <Input
            id={field?.name}
            ref={(el) => (inputsRef.current[index] = el)}
            value={localDate[field.name]}
            name={field.name}
            className={className}
            placeholder={field.label}
            maxLength={field.maxLength}
            onChange={(e) => onDateChange(e, index, field.maxLength)}
            onKeyDown={(e) => onKeyDown(e, index)}
            onBlur={onBlur}
            status={errorText && 'error'}
          />
        </Flex>
      ))}
    </Flex>
  );
};

export default DateInput;
